import { primary } from '@/styles/constants/colors';

import type {
  CornerPoint,
  CornerPoints,
  FrameState,
  GuideRect,
} from './documentScanner.types';

const GUIDE_RADIUS = 16;
const PROGRESS_COLOR = primary[300] ?? '#AAF2FD';

/**
 * ID-1 aspect ratio (ISO/IEC 7810): 85.6mm × 53.98mm ≈ 1.586:1
 */
const DOCUMENT_ASPECT_RATIO = 1.586;
const SHORT_LANDSCAPE_MAX_HEIGHT = 600;
const SHORT_LANDSCAPE_GUIDE_PADDING = 0.2;

export const CORNER_KEYS: (keyof CornerPoints)[] = [
  'topLeftCorner',
  'topRightCorner',
  'bottomLeftCorner',
  'bottomRightCorner',
];

// ── Guide frame ────────────────────────────────────────────

export function getGuideRect(
  canvasWidth: number,
  canvasHeight: number,
  padding: number = 0.15,
): GuideRect {
  const isPortrait = canvasHeight > canvasWidth;
  const aspectRatio = isPortrait
    ? 1 / DOCUMENT_ASPECT_RATIO
    : DOCUMENT_ASPECT_RATIO;

  const maxW = canvasWidth * (1 - 2 * padding);
  const maxH = canvasHeight * (1 - 2 * padding);

  let w: number;
  let h: number;

  if (maxW / aspectRatio <= maxH) {
    w = maxW;
    h = maxW / aspectRatio;
  } else {
    h = maxH;
    w = maxH * aspectRatio;
  }

  const x = (canvasWidth - w) / 2;
  const y = (canvasHeight - h) / 2;

  return { x, y, w, h };
}

export function getResponsiveGuidePadding(
  viewportWidth: number,
  viewportHeight: number,
  guidePadding: number = 0.15,
): number {
  const isShortLandscape =
    viewportWidth > viewportHeight &&
    viewportHeight <= SHORT_LANDSCAPE_MAX_HEIGHT;

  return isShortLandscape
    ? Math.max(guidePadding, SHORT_LANDSCAPE_GUIDE_PADDING)
    : guidePadding;
}

// ── Corner smoothing (EMA) ─────────────────────────────────

export function smoothCornerPoints(
  raw: CornerPoints,
  prev: CornerPoints | null,
  alpha: number = 0.3,
): CornerPoints {
  if (!prev) return structuredClone(raw);
  const result = {} as CornerPoints;
  for (const k of CORNER_KEYS) {
    result[k] = {
      x: prev[k].x + alpha * (raw[k].x - prev[k].x),
      y: prev[k].y + alpha * (raw[k].y - prev[k].y),
    };
  }
  return result;
}

// ── Stability detection ────────────────────────────────────

export function cornersClose(
  a: CornerPoints | null,
  b: CornerPoints | null,
  threshold: number,
): boolean {
  if (!a || !b) return false;
  return CORNER_KEYS.every(k => {
    const dx = a[k].x - b[k].x;
    const dy = a[k].y - b[k].y;
    return Math.hypot(dx, dy) < threshold;
  });
}

// ── Document validation ────────────────────────────────────

interface ValidationResult {
  valid: boolean;
  reason?: string;
}

export function isValidDocument(
  corners: CornerPoints,
  vw: number,
  vh: number,
  guidePadding: number = 0.15,
): ValidationResult {
  const guide = getGuideRect(vw, vh, guidePadding);

  const pts: CornerPoint[] = [
    corners.topLeftCorner,
    corners.topRightCorner,
    corners.bottomLeftCorner,
    corners.bottomRightCorner,
  ];
  if (pts.some(p => !p)) {
    return { valid: false, reason: 'incomplete' };
  }

  // Shoelace area: TL, TR, BR, BL
  const ordered = [pts[0], pts[1], pts[3], pts[2]];
  let contourArea = 0;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    contourArea += ordered[i].x * ordered[j].y;
    contourArea -= ordered[j].x * ordered[i].y;
  }
  contourArea = Math.abs(contourArea) / 2;

  const frameArea = vw * vh;
  const guideArea = guide.w * guide.h;

  if (contourArea < guideArea * 0.2) {
    return { valid: false, reason: 'too_small' };
  }
  if (contourArea > frameArea * 0.85) {
    return { valid: false, reason: 'too_large' };
  }

  const tolX = guide.w * 0.15;
  const tolY = guide.h * 0.15;
  const inBounds = pts.every(
    p =>
      p.x >= guide.x - tolX &&
      p.x <= guide.x + guide.w + tolX &&
      p.y >= guide.y - tolY &&
      p.y <= guide.y + guide.h + tolY,
  );
  if (!inBounds) {
    return { valid: false, reason: 'outside_frame' };
  }

  const angles = [
    cornerAngle(pts[2], pts[0], pts[1]),
    cornerAngle(pts[0], pts[1], pts[3]),
    cornerAngle(pts[1], pts[3], pts[2]),
    cornerAngle(pts[3], pts[2], pts[0]),
  ];
  const isRectangular = angles.every(a => a >= 60 && a <= 120);
  if (!isRectangular) {
    return { valid: false, reason: 'not_rectangular' };
  }

  return { valid: true };
}
// ── Geometry helpers ───────────────────────────────────────

/** Angle at vertex `b` formed by rays ba and bc, in degrees. */
function cornerAngle(a: CornerPoint, b: CornerPoint, c: CornerPoint): number {
  const ab = { x: b.x - a.x, y: b.y - a.y };
  const cb = { x: b.x - c.x, y: b.y - c.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const cross = ab.x * cb.y - ab.y * cb.x;
  return Math.abs(Math.atan2(cross, dot)) * (180 / Math.PI);
}

// ── Overlay drawing ────────────────────────────────────────

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  ccw?: boolean,
): void {
  if (ccw) {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x, y, x, y + r, r);
    ctx.arcTo(x, y + h, x + r, y + h, r);
    ctx.arcTo(x + w, y + h, x + w, y + h - r, r);
    ctx.arcTo(x + w, y, x + w - r, y, r);
    ctx.closePath();
  } else {
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}

/**
 * Draw a progress indicator as a bright border tracing clockwise
 * around the guide rectangle. `progress` ranges from 0 → 1.
 *
 * Traversal order (starting from top-left + radius):
 *   top edge → TR corner arc → right edge → BR corner arc →
 *   bottom edge → BL corner arc → left edge → TL corner arc
 */
function drawProgressBorder(
  ctx: CanvasRenderingContext2D,
  g: GuideRect,
  r: number,
  progress: number,
): void {
  if (progress <= 0) return;

  // Quarter-circle arc length
  const arcLen = (Math.PI / 2) * r;
  // Perimeter of the rounded rectangle (4 straight edges + 4 quarter arcs)
  const perimeter = 2 * (g.w - 2 * r) + 2 * (g.h - 2 * r) + 4 * arcLen;
  const targetLen = progress * perimeter;

  // Segment definitions in clockwise order
  //   { type: 'line', len }  — straight edge
  //   { type: 'arc',  cx, cy, startAngle }  — quarter-circle arc (always π/2)
  const segments: (
    | {
        type: 'line';
        x0: number;
        y0: number;
        x1: number;
        y1: number;
        len: number;
      }
    | { type: 'arc'; cx: number; cy: number; startAngle: number; len: number }
  )[] = [
    // Top edge (left→right)
    {
      type: 'line',
      x0: g.x + r,
      y0: g.y,
      x1: g.x + g.w - r,
      y1: g.y,
      len: g.w - 2 * r,
    },
    // TR corner
    {
      type: 'arc',
      cx: g.x + g.w - r,
      cy: g.y + r,
      startAngle: -Math.PI / 2,
      len: arcLen,
    },
    // Right edge (top→bottom)
    {
      type: 'line',
      x0: g.x + g.w,
      y0: g.y + r,
      x1: g.x + g.w,
      y1: g.y + g.h - r,
      len: g.h - 2 * r,
    },
    // BR corner
    {
      type: 'arc',
      cx: g.x + g.w - r,
      cy: g.y + g.h - r,
      startAngle: 0,
      len: arcLen,
    },
    // Bottom edge (right→left)
    {
      type: 'line',
      x0: g.x + g.w - r,
      y0: g.y + g.h,
      x1: g.x + r,
      y1: g.y + g.h,
      len: g.w - 2 * r,
    },
    // BL corner
    {
      type: 'arc',
      cx: g.x + r,
      cy: g.y + g.h - r,
      startAngle: Math.PI / 2,
      len: arcLen,
    },
    // Left edge (bottom→top)
    {
      type: 'line',
      x0: g.x,
      y0: g.y + g.h - r,
      x1: g.x,
      y1: g.y + r,
      len: g.h - 2 * r,
    },
    // TL corner
    { type: 'arc', cx: g.x + r, cy: g.y + r, startAngle: Math.PI, len: arcLen },
  ];

  ctx.save();
  ctx.strokeStyle = PROGRESS_COLOR;
  ctx.globalAlpha = 0.9;
  ctx.lineWidth = 8;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowColor = PROGRESS_COLOR;
  ctx.shadowBlur = 8;

  ctx.beginPath();
  ctx.moveTo(g.x + r, g.y);

  let drawn = 0;

  for (const seg of segments) {
    if (drawn >= targetLen) break;
    const remaining = targetLen - drawn;

    if (seg.type === 'line') {
      const fraction = Math.min(remaining / seg.len, 1);
      const ex = seg.x0 + (seg.x1 - seg.x0) * fraction;
      const ey = seg.y0 + (seg.y1 - seg.y0) * fraction;
      ctx.lineTo(ex, ey);
      drawn += seg.len * fraction;
    } else {
      const halfPi = Math.PI / 2;
      const fraction = Math.min(remaining / seg.len, 1);
      const endAngle = seg.startAngle + halfPi * fraction;
      ctx.arc(seg.cx, seg.cy, r, seg.startAngle, endAngle);
      drawn += seg.len * fraction;
    }
  }

  ctx.stroke();
  ctx.restore();
}

export function drawGuideFrame(
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  state: FrameState,
  progress: number,
  guidePadding: number = 0.15,
  guideLabel?: string,
): void {
  const g = getGuideRect(cw, ch, guidePadding);

  // Dim area outside the guide
  ctx.fillStyle = 'rgba(0, 0, 0, 0.45)';
  ctx.beginPath();
  ctx.rect(0, 0, cw, ch);
  roundRectPath(ctx, g.x, g.y, g.w, g.h, GUIDE_RADIUS, true);
  ctx.fill('evenodd');

  // Frame border color
  let strokeColor: string;
  let strokeAlpha = 1;
  let glowColor: string | null = null;
  let glowAlpha = 1;

  if (state === 'locking') {
    strokeColor = PROGRESS_COLOR;
    strokeAlpha = 0.4 + 0.4 * progress;
    glowColor = PROGRESS_COLOR;
    glowAlpha = 0.08 + 0.1 * progress;
  } else if (state === 'detected') {
    strokeColor = PROGRESS_COLOR;
    strokeAlpha = 0.6;
    glowColor = PROGRESS_COLOR;
    glowAlpha = 0.06;
  } else {
    strokeColor = 'rgba(255, 255, 255, 0.35)';
  }

  // Inner glow fill
  if (glowColor) {
    ctx.globalAlpha = glowAlpha;
    ctx.fillStyle = glowColor;
    ctx.beginPath();
    roundRectPath(ctx, g.x, g.y, g.w, g.h, GUIDE_RADIUS);
    ctx.fill();
  }

  // Border
  ctx.globalAlpha = strokeAlpha;
  ctx.strokeStyle = strokeColor;
  ctx.lineWidth = 3;
  ctx.beginPath();
  roundRectPath(ctx, g.x, g.y, g.w, g.h, GUIDE_RADIUS);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Progress border tracing the guide rectangle
  if (state === 'locking' && progress > 0) {
    drawProgressBorder(ctx, g, GUIDE_RADIUS, progress);
  }

  // Label text — scale font & gap relative to the guide frame so
  // the label looks consistent across screen sizes / device pixel ratios.
  if (state === 'idle') {
    const dpr = window.devicePixelRatio || 1;
    const fontSize = Math.max(Math.round(14 * dpr), Math.round(g.w * 0.02));
    const labelY = Math.max(fontSize + 16 * dpr, g.y - 16 * dpr);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.font = `500 ${fontSize}px Poppins, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(guideLabel ?? '', cw / 2, labelY);
  }
}
